import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import {
  ListChecks, AlertTriangle, Clock, MessageSquarePlus, ChevronRight, X,
  Wrench, ClipboardList, FileText, FileSearch, Receipt, UserCheck,
  CalendarClock, ShieldCheck, Package, Send, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const SKIP_KEY = 'fmflexipro_skip_mytasks_modal';   // sessionStorage — resets next login

const TYPE_META = {
  complaint:        { Icon: ClipboardList, label: 'Complaint',         color: 'text-rose-600 dark:text-rose-400' },
  work_order:       { Icon: Wrench,        label: 'Work Order',        color: 'text-amber-600 dark:text-amber-400' },
  ppm_task:         { Icon: FileText,      label: 'PPM Task',          color: 'text-sky-600 dark:text-sky-400' },
  schedule:         { Icon: CalendarClock, label: 'Schedule',          color: 'text-indigo-600 dark:text-indigo-400' },
  inspection:       { Icon: FileSearch,    label: 'Inspection / Snag', color: 'text-fuchsia-600 dark:text-fuchsia-400' },
  invoice:          { Icon: Receipt,       label: 'Invoice / Payment', color: 'text-emerald-600 dark:text-emerald-400' },
  trial_approval:   { Icon: UserCheck,     label: 'Trial Approval',    color: 'text-violet-600 dark:text-violet-400' },
  amc:              { Icon: ShieldCheck,   label: 'AMC Contract',      color: 'text-teal-600 dark:text-teal-400' },
  certificate:      { Icon: ShieldCheck,   label: 'Certificate',       color: 'text-cyan-600 dark:text-cyan-400' },
  stock:            { Icon: Package,       label: 'Low Stock',         color: 'text-orange-600 dark:text-orange-400' },
};

const PRIO_BADGE = {
  urgent:   'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  critical: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  high:     'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  medium:   'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  low:      'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
};

export default function MyTasksDialog({ open, onOpenChange }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: { total: 0, critical: 0, overdue: 0, by_type: {} }, tasks: [] });
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);     // {type, id}
  const [notes, setNotes] = useState([]);             // notes for expanded task
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);
  const [skipNext, setSkipNext] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/my-tasks`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tasks');
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { if (open) fetchTasks(); }, [open, fetchTasks]);

  const expandTask = async (t) => {
    if (expanded?.id === t.id && expanded?.type === t.type) {
      setExpanded(null); setNotes([]); return;
    }
    setExpanded({ id: t.id, type: t.type, link: t.link, title: t.title });
    setNotes([]); setNoteText('');
    try {
      const r = await axios.get(`${API}/my-tasks/notes`, {
        params: { task_type: t.type, task_id: t.id },
        withCredentials: true,
      });
      setNotes(r.data.notes || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load notes');
    }
  };

  const postNote = async () => {
    if (!expanded || !noteText.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/my-tasks/notes`, {
        task_type: expanded.type, task_id: expanded.id, content: noteText.trim(),
      }, { withCredentials: true });
      setNotes([r.data.note, ...notes]);
      setNoteText('');
      toast.success('Note added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post note'); }
    setPosting(false);
  };

  const handleClose = (next) => {
    if (!next && skipNext) sessionStorage.setItem(SKIP_KEY, '1');
    onOpenChange(next);
  };

  const goTo = (link) => {
    onOpenChange(false);
    if (link) navigate(link);
  };

  const visible = (data.tasks || []).filter(t => filter === 'all' ? true : t.type === filter);
  const types = Object.entries(data.summary?.by_type || {});

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="my-tasks-dialog" className="max-w-3xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ListChecks className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            My Tasks
            {data.summary?.total > 0 && (
              <span className="text-sm font-normal text-muted-foreground">— {data.summary.total} pending</span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Pending tasks assigned to you across the system</DialogDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {data.summary?.overdue > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
                <Clock className="w-3 h-3" /> {data.summary.overdue} overdue
              </span>
            )}
            {data.summary?.critical > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
                <AlertTriangle className="w-3 h-3" /> {data.summary.critical} critical
              </span>
            )}
          </div>
          {/* Filter pills */}
          <div className="flex flex-wrap gap-1.5 pt-3">
            <button
              data-testid="mytasks-filter-all"
              onClick={() => setFilter('all')}
              className={`text-xs px-3 py-1 rounded-full font-medium transition ${
                filter === 'all'
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-foreground hover:bg-muted/70 border border-border'
              }`}
            >All ({data.summary?.total || 0})</button>
            {types.map(([t, count]) => {
              const meta = TYPE_META[t];
              if (!meta) return null;
              const Ico = meta.Icon;
              return (
                <button
                  key={t}
                  data-testid={`mytasks-filter-${t}`}
                  onClick={() => setFilter(t)}
                  className={`text-xs px-3 py-1 rounded-full font-medium transition inline-flex items-center gap-1 ${
                    filter === t
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-foreground hover:bg-muted/70 border border-border'
                  }`}
                >
                  <Ico className="w-3 h-3" /> {meta.label} ({count})
                </button>
              );
            })}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2 bg-background">
          {loading && (
            <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading your tasks…</div>
          )}
          {!loading && visible.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">
              <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <div className="text-foreground font-medium">All caught up!</div>
              <div className="text-xs mt-1">No pending tasks assigned to you right now.</div>
            </Card>
          )}
          {!loading && visible.map((t) => {
            const meta = TYPE_META[t.type] || {};
            const Ico = meta.Icon || ListChecks;
            const isExpanded = expanded?.id === t.id && expanded?.type === t.type;
            return (
              <Card
                key={`${t.type}-${t.id}`}
                data-testid={`mytasks-row-${t.type}-${t.id}`}
                className={`p-3 border ${t.is_overdue ? 'border-rose-300 dark:border-rose-700' : 'border-border'}`}
              >
                <div className="flex items-start gap-3">
                  <Ico className={`w-5 h-5 mt-0.5 shrink-0 ${meta.color || ''}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{meta.label || t.type}</span>
                      {t.ref && <span className="font-mono text-xs text-muted-foreground">{t.ref}</span>}
                      {t.priority && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${PRIO_BADGE[t.priority] || PRIO_BADGE.low}`}>
                          {t.priority}
                        </span>
                      )}
                      {t.is_overdue && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-rose-600 text-white">OVERDUE</span>
                      )}
                      {t.needs_triage && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white" data-testid="needs-triage-badge">NEEDS TRIAGE</span>
                      )}
                    </div>
                    <div className="font-medium text-foreground mt-0.5">{t.title}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>}
                    <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-muted-foreground">
                      {t.due_date && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due {new Date(t.due_date).toLocaleDateString()}
                          {t.days_until_due !== undefined && t.days_until_due !== null && (
                            <span className={t.is_overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : ''}>
                              {' '}({t.days_until_due === 0 ? 'today' : `${t.days_until_due}d`})
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      data-testid={`mytasks-open-${t.type}-${t.id}`}
                      onClick={() => goTo(t.link)}
                    >
                      <ChevronRight className="w-4 h-4 mr-1" /> Open
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      data-testid={`mytasks-notes-${t.type}-${t.id}`}
                      onClick={() => expandTask(t)}
                    >
                      <MessageSquarePlus className="w-4 h-4 mr-1" /> Notes
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {notes.length === 0 && (
                        <div className="text-xs text-muted-foreground italic">No notes yet — be the first.</div>
                      )}
                      {notes.map((n) => (
                        <div key={n.id} className="text-xs rounded-md bg-muted p-2">
                          <div className="flex justify-between text-muted-foreground">
                            <strong className="text-foreground">{n.author_name || 'User'}</strong>
                            <span>{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span>
                          </div>
                          <div className="text-foreground whitespace-pre-wrap mt-0.5">{n.content}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Textarea
                        rows={2}
                        data-testid={`mytasks-note-input-${t.type}-${t.id}`}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note (visible to task owner + assignees)…"
                        className="text-sm"
                      />
                      <Button
                        size="sm"
                        data-testid={`mytasks-note-submit-${t.type}-${t.id}`}
                        onClick={postNote}
                        disabled={posting || !noteText.trim()}
                        className="bg-amber-500 hover:bg-amber-600 text-slate-900 self-end"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t border-border bg-muted/30 flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
            <input
              type="checkbox"
              data-testid="mytasks-skip-checkbox"
              checked={skipNext}
              onChange={(e) => setSkipNext(e.target.checked)}
              className="rounded border-border"
            />
            Don't show again this session
          </label>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="mytasks-close-btn">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { SKIP_KEY };
